import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import {
  gpsDrivePowerCutAlertService,
  type PowerCutAlertSettings,
} from "@/services/gpsdrive-power-cut-alert.service";

export const Route = createFileRoute("/admin/power-cut-alert")({
  component: () => (
    <AdminAccessGate action="comunicacao">
      <PowerCutAlertPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Corte de Energia — Administração — Wp Rastreamento" }] }),
});

// Espelha POWER_CUT_TEMPLATE_VARIABLES no backend
// (src/power-cut-alert/power-cut-alert-template.ts) — qualquer variável nova
// precisa ser adicionada nos dois lugares.
const TEMPLATE_VARIABLES: { key: string; label: string; example: string }[] = [
  { key: "cliente", label: "Nome do cliente", example: "Cliente Teste" },
  { key: "veiculo", label: "Veículo (modelo)", example: "Fiat Toro" },
  { key: "placa", label: "Placa", example: "ABC1D23" },
  { key: "alerta", label: "Tipo do alerta", example: "corte de energia (bateria violada)" },
  { key: "data", label: "Data/hora do alerta", example: "08/10/2026 14:30" },
  { key: "mapa", label: "Link do local no mapa", example: "https://maps.google.com/?q=-23.55052,-46.63331" },
];

const DEFAULT_TEMPLATE =
  "🚨 *Alerta do rastreador*\n\n" +
  "O veículo {{veiculo}} placa *{{placa}}* informou *{{alerta}}* em {{data}}.\n\n" +
  "Local: {{mapa}}\n\n" +
  "Se não foi você, verifique o veículo ou fale com a central.";

const QUERY_KEY = ["admin", "power-cut-alert"] as const;

function renderPreview(template: string): string {
  return TEMPLATE_VARIABLES.reduce((text, v) => text.replaceAll(`{{${v.key}}}`, v.example), template);
}

function PowerCutAlertPage() {
  const qc = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => gpsDrivePowerCutAlertService.get(),
    retry: false,
  });
  const update = useMutation({
    mutationFn: (input: PowerCutAlertSettings) => gpsDrivePowerCutAlertService.update(input),
    onSuccess: (data) => qc.setQueryData(QUERY_KEY, data),
  });
  const testSend = useMutation({
    mutationFn: (input: { phone: string; messageTemplate?: string }) => gpsDrivePowerCutAlertService.testSend(input),
  });

  const [enabled, setEnabled] = useState(false);
  const [cooldownMinutes, setCooldownMinutes] = useState("30");
  const [messageTemplate, setMessageTemplate] = useState(DEFAULT_TEMPLATE);
  const [testPhone, setTestPhone] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    setCooldownMinutes(String(settings.cooldownMinutes));
    setMessageTemplate(settings.messageTemplate || DEFAULT_TEMPLATE);
  }, [settings]);

  const insertVariable = (key: string) => {
    const textarea = textareaRef.current;
    const token = `{{${key}}}`;
    if (!textarea) {
      setMessageTemplate((prev) => prev + token);
      return;
    }
    const start = textarea.selectionStart ?? messageTemplate.length;
    const end = textarea.selectionEnd ?? messageTemplate.length;
    setMessageTemplate(messageTemplate.slice(0, start) + token + messageTemplate.slice(end));
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleSave = async () => {
    const cooldown = Number(cooldownMinutes);
    if (!Number.isInteger(cooldown) || cooldown < 5 || cooldown > 1440) {
      toast.error("O intervalo entre avisos deve ser de 5 a 1440 minutos");
      return;
    }
    if (!messageTemplate.trim()) {
      toast.error("Informe o texto do aviso");
      return;
    }
    try {
      await update.mutateAsync({ enabled, cooldownMinutes: cooldown, messageTemplate: messageTemplate.trim() });
      toast.success("Configuração de Corte de Energia salva");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar a configuração");
    }
  };

  const handleTestSend = async () => {
    if (!testPhone.trim()) {
      toast.error("Informe um número de WhatsApp para o teste");
      return;
    }
    try {
      await testSend.mutateAsync({ phone: testPhone.trim(), messageTemplate: messageTemplate.trim() || undefined });
      toast.success("Mensagem de teste enviada");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao enviar o teste");
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Corte de Energia</h1>
        <p className="text-sm text-muted-foreground">
          Avisa por WhatsApp o cliente (e as pessoas autorizadas dele) quando o rastreador informa corte
          de energia, bateria violada ou violação do equipamento.
        </p>
      </div>

      <GpsDriveSessionBanner />

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Configuração</CardTitle>
          <CardDescription>
            Vale para todos os veículos da empresa. Requer o WhatsApp conectado em Administração &gt;
            Conta &amp; Integrações &gt; Integrações. Recebe quem tem celular na aba Cliente e as pessoas
            autorizadas marcadas para "Corte de energia".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Avisar cliente sobre corte de energia</p>
              <p className="text-xs text-muted-foreground">
                Enviado só quando o rastreador informa corte de energia (bateria violada).
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={isLoading} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pc-cooldown">Intervalo mínimo entre avisos do mesmo veículo (minutos)</Label>
            <Input
              id="pc-cooldown"
              type="number"
              min={5}
              max={1440}
              value={cooldownMinutes}
              onChange={(e) => setCooldownMinutes(e.target.value)}
              disabled={isLoading}
              className="max-w-[160px]"
            />
            <p className="text-xs text-muted-foreground">
              Sem energia, o rastreador repete o alarme a cada poucos minutos — isso evita mandar
              várias mensagens seguidas para o mesmo corte.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Texto do aviso</CardTitle>
          <CardDescription>
            Clique em uma variável para inserir no texto. Elas são substituídas pelos dados reais na
            hora do envio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_VARIABLES.map((v) => (
              <Badge
                key={v.key}
                variant="secondary"
                className="cursor-pointer select-none hover:bg-secondary/70"
                onClick={() => insertVariable(v.key)}
                title={v.label}
              >
                {`{{${v.key}}}`}
              </Badge>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="pc-message-template">Mensagem enviada</Label>
            <Textarea
              id="pc-message-template"
              ref={textareaRef}
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              disabled={isLoading}
              rows={7}
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Pré-visualização (com dados de exemplo)</Label>
            <div className="whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm">
              {renderPreview(messageTemplate) || "—"}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={update.isPending || isLoading}>
              {update.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Testar envio</CardTitle>
          <CardDescription>
            Envia o texto acima (mesmo que ainda não salvo) com dados de exemplo para um número de
            WhatsApp à sua escolha.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pc-test-phone">Número de WhatsApp para teste</Label>
            <Input
              id="pc-test-phone"
              type="tel"
              placeholder="(11) 99999-9999"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="max-w-[220px]"
            />
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleTestSend} disabled={testSend.isPending}>
              {testSend.isPending ? "Enviando…" : "Enviar teste"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
